import { useLocation, useParams, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, Phone, Copy, ArrowRight, DeviceMobile, Gift, WhatsappLogo, ChatCircle, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { playSound } from "@/lib/sounds";
import GoldConfetti from "@/components/GoldConfetti";
import WhatsAppBookingButton from "@/components/WhatsAppBookingButton";

export default function ReservationConfirmed() {
  const { ref } = useParams();
  const location = useLocation();
  const state = location.state || {};
  const isMomo = state.mode === "mobile_money";
  const [isGift, setIsGift] = useState(!!state.is_gift);
  const [recipientName, setRecipientName] = useState(state.gift_recipient_name || "");
  const [recipientPhone, setRecipientPhone] = useState(state.gift_recipient_phone || "");
  const [celebrate, setCelebrate] = useState(false);
  const shareRowRef = useRef(null);

  // If the visitor lands here directly (e.g. from Stripe redirect) without full state,
  // fetch our booking to enrich gift metadata (is_gift, recipient info).
  useEffect(() => {
    if (state.is_gift !== undefined && state.gift_recipient_name) return;
    api.get(`/bookings/me`)
      .then(r => {
        const b = (r.data || []).find(x => x.reservation_ref === ref);
        if (!b) return;
        if (b.is_gift) setIsGift(true);
        if (b.gift_recipient_name) setRecipientName(b.gift_recipient_name);
        if (b.gift_recipient_phone) setRecipientPhone(b.gift_recipient_phone);
      })
      .catch(() => {});
  }, [ref, state.is_gift, state.gift_recipient_name]);

  // Diaspora "love landed" celebration — plays once per reservation ref for paid gifts.
  useEffect(() => {
    if (!isGift || !state.paid) return;
    const seenKey = `ridexelite:gift-celebrated:${ref}`;
    if (sessionStorage.getItem(seenKey)) return;
    sessionStorage.setItem(seenKey, "1");
    setCelebrate(true);
    playSound("coin");
    // Second chime a beat later for a warmer two-note landing
    const t = setTimeout(() => playSound("coin"), 380);
    const off = setTimeout(() => setCelebrate(false), 5200);
    // After confetti settles, gently scroll the share row into view
    const scroll = setTimeout(() => {
      if (shareRowRef.current) {
        shareRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 2200);
    return () => { clearTimeout(t); clearTimeout(off); clearTimeout(scroll); };
  }, [isGift, state.paid, ref]);

  const copy = () => {
    navigator.clipboard.writeText(ref);
    toast.success("Reservation code copied");
  };

  // Build the share message + shareable URL for the "Share the gift card" row.
  // We use the backend /api/share/gift/{ref} endpoint so link previews on
  // WhatsApp / iMessage / Facebook render the branded per-service OG card
  // (with the service name, price, and unity crest) instead of the generic
  // site preview. That endpoint auto-redirects humans to /gift/{ref}.
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const shareUrl = API_BASE
    ? `${API_BASE}/api/share/gift/${ref}`
    : (typeof window !== "undefined" ? `${window.location.origin}/gift/${ref}` : `/gift/${ref}`);
  const shareGreeting = recipientName ? `Hi ${recipientName.split(" ")[0]}` : "Hi love";
  const shareMessage = `${shareGreeting} 💌 I just sent you a little something via RideXelite — open your gift card here: ${shareUrl}`;
  const phoneDigits = (recipientPhone || "").replace(/[^\d]/g, "");
  const waHref = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(shareMessage)}`
    : `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
  const smsHref = phoneDigits
    ? `sms:${recipientPhone}?body=${encodeURIComponent(shareMessage)}`
    : `sms:?body=${encodeURIComponent(shareMessage)}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Gift card link copied");
    } catch {
      toast.error("Couldn't copy — try again");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="reservation-confirmed-page">
      {celebrate && <GoldConfetti />}
      <CheckCircle size={72} weight="fill" className="text-[#D4AF37] mx-auto" />
      <div className="eyebrow gold-text mt-6">
        {state.paid ? "Payment received" : isMomo ? "Complete your Mobile Money payment" : "Reservation confirmed"}
      </div>
      <h1 className="font-serif-display text-4xl lg:text-5xl text-white mt-4">
        {isGift && state.paid
          ? "Love just landed in Monrovia."
          : state.paid
          ? "Your booking is paid."
          : isMomo
          ? "One more step."
          : "Your ride is reserved."}
      </h1>
      {isGift && state.paid && (
        <p data-testid="gift-celebration-note" className="mt-4 text-sm text-zinc-400 max-w-xl mx-auto">
          We&apos;ll reach out to your recipient within 24 hours to confirm timing. Share the gift card below so they know it&apos;s from you.
        </p>
      )}

      {isGift && state.paid && (
        <div
          ref={shareRowRef}
          data-testid="gift-share-row"
          className="mt-10 rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#161014] via-[#0F0F11] to-[#1a1610] p-6 sm:p-8 text-left shadow-[0_20px_60px_-20px_rgba(212,175,55,0.35)]"
        >
          <div className="flex items-center gap-2 mb-1">
            <Gift weight="fill" size={16} className="text-[#D4AF37]" />
            <div className="eyebrow gold-text">Tell them it's from you</div>
          </div>
          <h3 className="font-serif-display text-xl sm:text-2xl text-white leading-tight">
            {recipientName ? (
              <>Send <span className="gold-text italic">{recipientName.split(" ")[0]}</span> the gift card</>
            ) : (
              <>Share the gift card with your recipient</>
            )}
          </h3>
          <p className="mt-2 text-sm text-zinc-400">
            One tap — we&apos;ve pre-filled the message and the shareable card link.
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-whatsapp-btn"
              data-sound="coin"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#25D366] text-black text-sm font-semibold hover:brightness-110 transition"
            >
              <WhatsappLogo weight="fill" size={18} /> WhatsApp
            </a>
            <a
              href={smsHref}
              data-testid="share-sms-btn"
              data-sound="coin"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37] text-sm font-semibold hover:bg-[#D4AF37] hover:text-black transition"
            >
              <ChatCircle weight="fill" size={18} /> SMS
            </a>
            <button
              type="button"
              onClick={copyLink}
              data-testid="share-copy-link-btn"
              data-sound="coin"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-white/15 text-white text-sm font-semibold hover:border-white/40 hover:bg-white/5 transition"
            >
              <LinkSimple weight="bold" size={18} /> Copy link
            </button>
          </div>

          <div className="mt-4 rounded-xl bg-black/40 border border-white/10 p-3 text-xs text-zinc-400 leading-relaxed">
            <span className="text-zinc-500">Message preview · </span>
            <span className="text-zinc-200">{shareMessage}</span>
          </div>
        </div>
      )}

      <div className="mt-12 rounded-3xl border border-[#D4AF37]/30 bg-[#121214] p-10">
        <div className="eyebrow mb-3">Reservation code</div>
        <button
          onClick={copy}
          data-testid="copy-reservation-ref"
          className="inline-flex items-center gap-3 font-serif-display text-3xl lg:text-4xl text-white hover:text-[#D4AF37] transition"
        >
          <span className="tracking-widest">{ref}</span>
          <Copy size={20} />
        </button>

        {state.dispatcher_name && (
          <div className="mt-10 rounded-2xl border border-white/10 bg-black/40 p-6 text-left">
            <div className="eyebrow gold-text">Contact your dispatcher</div>
            <div className="text-white font-serif-display text-2xl mt-2">{state.dispatcher_name}</div>
            <a
              href={`tel:${(state.dispatcher_phone || "").replace(/\s/g, "")}`}
              data-testid="reservation-dispatcher-phone"
              className="mt-3 inline-flex items-center gap-2 text-white hover:text-[#D4AF37] transition"
            >
              <Phone weight="duotone" size={18} className="text-[#D4AF37]" />
              {state.dispatcher_phone}
            </a>
            <div className="mt-4">
              <WhatsAppBookingButton
                dispatcherName={state.dispatcher_name}
                dispatcherPhone={state.dispatcher_phone}
                subject={`Hi ${state.dispatcher_name}, I have a RideXelite reservation and would like to confirm details`}
                lines={[
                  `Reservation: ${ref}`,
                  state.service_name ? `Service: ${state.service_name}` : "",
                  state.scheduled_at ? `Scheduled: ${new Date(state.scheduled_at).toLocaleString()}` : "",
                  state.address ? `Pickup / address: ${state.address}` : "",
                  state.amount_usd ? `Amount: $${(typeof state.amount_usd === 'number' ? state.amount_usd.toFixed(2) : state.amount_usd)} USD` : "",
                  state.paid ? "Payment: Received" : (isMomo ? "Payment: Mobile Money — pending confirmation" : "Payment: On arrival"),
                  isGift && recipientName ? `Gift recipient: ${recipientName}${recipientPhone ? ` (${recipientPhone})` : ""}` : "",
                ]}
                testId="reservation-whatsapp-btn"
              />
            </div>
            <p className="mt-4 text-sm text-zinc-400">
              Please quote your reservation code above when calling to confirm details or make changes.
            </p>
          </div>
        )}

        {isMomo && state.dispatcher_phone && (
          <div className="mt-8 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#1a1610] via-black to-[#1a1815] p-6 text-left" data-testid="momo-instructions">
            <div className="flex items-center gap-2 mb-3">
              <DeviceMobile weight="duotone" size={20} className="text-[#D4AF37]" />
              <div className="eyebrow gold-text">{state.provider_label || "Mobile Money"} · Payment instructions</div>
            </div>
            <ol className="space-y-3 text-sm text-zinc-300 list-decimal list-inside">
              <li>Dial your Mobile Money menu on your phone.</li>
              <li>Send <span className="text-white font-semibold">USD {state.amount_usd?.toFixed?.(2) || state.amount_usd}</span> to <span className="text-white font-semibold">{state.dispatcher_phone}</span> ({state.dispatcher_name}).</li>
              <li>In the reference / note field, quote your reservation code: <span className="text-[#D4AF37] font-semibold">{ref}</span>.</li>
              <li>You'll receive a booking-confirmed notification once we verify the transfer.</li>
            </ol>
          </div>
        )}

        <p className="mt-8 text-sm text-zinc-500">
          {state.paid
            ? "Your payment has been received via PayPal. A confirmation is in your dashboard."
            : isMomo
            ? "We'll confirm your booking as soon as the Mobile Money transfer is received."
            : "Payment will be collected on arrival. Keep the reservation code with you."}
        </p>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
        {isGift && (
          <Link
            to={`/gift/${ref}`}
            data-testid="reservation-view-gift-card"
            className="px-6 py-3 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB] text-black text-sm font-semibold inline-flex items-center gap-2 shadow-[0_10px_35px_-10px_rgba(212,175,55,0.6)]"
          >
            <Gift weight="fill" size={14} /> View shareable gift card
          </Link>
        )}
        <Link
          to="/dashboard"
          data-testid="reservation-goto-dashboard"
          className="px-6 py-3 rounded-full bg-[#D4AF37] text-black text-sm font-semibold inline-flex items-center gap-2"
        >
          View my bookings <ArrowRight size={14} weight="bold" />
        </Link>
        <Link to="/" className="px-6 py-3 rounded-full border border-white/20 text-sm text-white">
          Home
        </Link>
      </div>
    </div>
  );
}
